import { Check } from "lucide-react";
import type { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

import { STATUS_META } from "../status";
import type { WorkflowDef, WorkflowView } from "../types";

/**
 * The vertical stepper beside the Case Automations cards: one rail node per
 * step, joined by a line that runs down the whole list, through each section
 * group's header row and across the gap between groups, so the page reads as
 * steps across the groups. Replaces the horizontal segment bar.
 *
 * The cards sit in a two-column grid (one column when the pane is narrow), so
 * the rail follows the cards. Narrow: one node per card, each at its card's
 * title. Wide: one node per row of cards, showing the row's most pressing
 * state (the other card of a pair has no node of its own, it still has its
 * "Step N" line for screen readers). Every step stays in the list, in reading
 * order, whichever way the grid falls.
 */

export type StepState =
  | "done"
  | "next"
  | "running"
  | "attention"
  | "failed"
  | "partial"
  | "todo";

/** Where one step stands: Next Up and running read in accent, trouble in color. */
export const stepState = (view: WorkflowView, nextUp: boolean): StepState => {
  if (view.status === "running") return "running";
  if (nextUp) return "next";
  if (view.status === "failed") return "failed";
  if (view.status === "needs-you" || view.status === "stale") {
    return "attention";
  }
  return view.status === "done" ? "done" : "todo";
};

/** A row of cards as one node: the most pressing state wins. */
export const rowState = (states: StepState[]): StepState => {
  for (const s of ["running", "next", "failed", "attention"] as const) {
    if (states.includes(s)) return s;
  }
  if (states.every((s) => s === "done")) return "done";
  return states.includes("done") ? "partial" : "todo";
};

const STATE_LABEL: Record<StepState, string> = {
  done: "Done",
  next: "Next Up",
  running: "Running",
  attention: "Needs Attention",
  failed: "Failed",
  partial: "In Progress",
  todo: "To Do"
};

/** "Step 1, Retitle Exhibits, Done": the step's accessible name. */
export const stepLabel = (
  n: number,
  def: WorkflowDef,
  view: WorkflowView,
  state: StepState
): string => {
  const label =
    state === "todo" || state === "attention" || state === "failed"
      ? STATUS_META[view.status].label
      : STATE_LABEL[state];
  return `Step ${n}, ${def.title}${
    def.optional ? " (Optional)" : ""
  }, ${label}`;
};

const ORANGE = "border-orange dark:border-warn";

/** The node's 2px outline and 4px halo, as variables the utilities read. */
const LINE = "border-(length:--wf-node-line)";
const HALO = "ring-(length:--wf-node-halo)";

const NODE: Record<StepState, string> = {
  done: "bg-success text-on-accent",
  next: `${LINE} border-accent-seed bg-surface ${HALO} ring-accent-soft`,
  running: `${LINE} border-accent-seed bg-surface ${HALO} ring-accent-soft`,
  attention: `${LINE} ${ORANGE} bg-surface`,
  failed: `${LINE} border-danger bg-surface`,
  partial: `${LINE} border-success bg-[linear-gradient(to_right,var(--success)_50%,var(--surface)_50%)]`,
  todo: `${LINE} border-line-4 bg-surface`
};

const DOT: Partial<Record<StepState, string>> = {
  next: "bg-accent-seed",
  running: "bg-accent-seed motion-safe:animate-pulse"
};

/** The 16px circle: filled green with a check when done, hollow otherwise. */
const StepNode: FC<{ state: StepState; className?: string }> = ({
  state,
  className
}) => (
  <span
    aria-hidden
    data-state={state}
    data-slot="workflow-step-node"
    className={cn(
      "relative z-10 flex size-4 shrink-0 items-center justify-center rounded-full [--wf-node-halo:4px] [--wf-node-line:2px]",
      NODE[state],
      className
    )}
  >
    {state === "done" ? (
      <Check className="size-2.5" strokeWidth={3.5} />
    ) : DOT[state] ? (
      <span className={cn("size-1.5 rounded-full", DOT[state])} />
    ) : null}
  </span>
);

/** The rail's column; the cards grid and each group's header row share it. */
export const RAIL_COLS = "grid-cols-[1rem_minmax(0,1fr)] gap-x-3";

/**
 * Line ends, narrow (one card per row) and wide (`@xl`, two per row). Each
 * piece of line starts at the section's top or node center, and runs on over
 * the 0.5rem row gap, or out of its group (the group's bottom padding, the gap
 * between groups and the next group's top padding: `--wf-bridge`) into the
 * next group's header piece, which starts flush, so the translucent line never
 * doubles up. The groups must not be positioned or clip, so the line paints
 * over their backgrounds.
 */
const TOP = {
  open: "top-0",
  first: "top-(--wf-center)",
  join: "top-0"
} as const;
const TOP_WIDE = {
  open: "@xl:top-0",
  first: "@xl:top-(--wf-center)",
  join: "@xl:top-0"
} as const;
const BOTTOM = {
  row: "-bottom-2",
  section: "bottom-(--wf-bridge)",
  last: "bottom-[calc(100%-var(--wf-center))]"
} as const;
const BOTTOM_WIDE = {
  row: "@xl:-bottom-2",
  section: "@xl:bottom-(--wf-bridge)",
  last: "@xl:bottom-[calc(100%-var(--wf-center))]"
} as const;

type Edge = {
  /** Where this cell's line begins. */
  top: keyof typeof TOP;
  /** Where it ends: into the next row, the next section, or at its node. */
  bottom: keyof typeof BOTTOM;
};

/**
 * Where the line starts and ends for the card at `index` of `count` in a
 * section, narrow and wide. The last card overall ends its line at its node.
 */
export const stepEdges = (
  index: number,
  count: number,
  firstSection: boolean,
  lastSection: boolean
): { narrow: Edge; wide: Edge } => {
  const narrow: Edge = {
    top: index === 0 ? (firstSection ? "first" : "open") : "join",
    bottom: index === count - 1 ? (lastSection ? "last" : "section") : "row"
  };
  const lastRow = Math.floor((count - 1) / 2);
  const row = Math.floor(index / 2);
  const wide: Edge = {
    top: row === 0 ? (firstSection ? "first" : "open") : "join",
    bottom: row === lastRow ? (lastSection ? "last" : "section") : "row"
  };
  return { narrow, wide };
};

type RailProps = {
  /** 1-based, across the whole list. */
  number: number;
  def: WorkflowDef;
  view: WorkflowView;
  state: StepState;
  /** The card's row in the wide grid, as one node (the first of a pair). */
  wideState?: StepState;
  edges: ReturnType<typeof stepEdges>;
  /** The second card of a pair has no node of its own in the wide grid. */
  pairedWide: boolean;
};

/** The rail cell beside one card: its piece of line and its node. */
const StepRail: FC<RailProps> = ({
  number,
  def,
  view,
  state,
  wideState,
  edges,
  pairedWide
}) => {
  const wide = wideState ?? state;
  const split = wide !== state;
  return (
    <div
      className={cn("relative", pairedWide && "@xl:sr-only")}
      data-slot="workflow-step-rail"
    >
      <span
        aria-hidden
        className={cn(
          "absolute left-1/2 w-px -translate-x-1/2 bg-line-3",
          TOP[edges.narrow.top],
          TOP_WIDE[edges.wide.top],
          BOTTOM[edges.narrow.bottom],
          BOTTOM_WIDE[edges.wide.bottom]
        )}
      />
      {/* The node sits on the card's title row: its padding, then its height. */}
      <div className="flex h-(--wf-row) items-center justify-center mt-(--wf-pad)">
        <StepNode state={state} className={split ? "@xl:hidden" : undefined} />
        {split ? <StepNode state={wide} className="hidden @xl:flex" /> : null}
      </div>
      <span className="sr-only">{stepLabel(number, def, view, state)}</span>
    </div>
  );
};

type StepProps = Omit<RailProps, "pairedWide"> & {
  index: number;
  children: ReactNode;
};

/**
 * One step: a list item (so the steps count and read in order) holding its rail
 * cell and its card as two cells of the section's grid. `aria-current="step"`
 * marks Next Up.
 */
export const WorkflowStep: FC<StepProps> = ({ index, children, ...rail }) => (
  // `display: contents` drops the item's role in WebKit; keep it explicit.
  // eslint-disable-next-line jsx-a11y/no-redundant-roles
  <li
    role="listitem"
    className="contents"
    data-testid={`workflow-step-${rail.def.id}`}
    data-state={rail.state}
    aria-current={rail.state === "next" ? "step" : undefined}
  >
    <StepRail {...rail} pairedWide={index % 2 === 1} />
    {children}
  </li>
);
